import React, { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Mail } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useToast } from "../../hooks/use-toast";

const PREFS = [
  { key: "enquiries", label: "New enquiries" },
  { key: "quotes", label: "Quote requests" },
  { key: "careers", label: "Careers / CV applications" },
  { key: "guides", label: "Guide downloads" },
  { key: "legal_cashiering", label: "LC checklist" },
  { key: "health_check", label: "Health Check" },
  { key: "weekly", label: "Weekly report" },
  { key: "weekly_leads", label: "Weekly lead digest" },
];

export default function NotificationRecipientsManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () => adminApi.notificationRecipients.list().then((d) => { setItems(d); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!email.trim()) { toast({ title: "Enter an email address", variant: "destructive" }); return; }
    setAdding(true);
    try {
      const rec = await adminApi.notificationRecipients.add({ name, email, prefs: Object.fromEntries(PREFS.map((p) => [p.key, true])) });
      setItems([...items, rec]);
      setName(""); setEmail("");
      toast({ title: "Recipient added" });
    } catch (e) { toast({ title: "Failed to add", variant: "destructive" }); }
    finally { setAdding(false); }
  };

  const togglePref = async (item, key) => {
    const prefs = { ...item.prefs, [key]: !item.prefs?.[key] };
    setItems(items.map((i) => (i.id === item.id ? { ...i, prefs } : i)));
    try { await adminApi.notificationRecipients.update(item.id, { prefs }); }
    catch (e) { toast({ title: "Save failed", variant: "destructive" }); load(); }
  };

  const remove = async (id) => {
    setItems(items.filter((i) => i.id !== id));
    await adminApi.notificationRecipients.remove(id);
  };

  if (loading) return <div className="text-slate-400 p-6">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-bold text-navy flex items-center gap-2"><Mail size={18} /> Notification recipients</h3>
        <p className="text-sm text-slate-500 mt-1">Add the people who should receive notification emails, then tick which emails each person gets. If no one is ticked for a type, it falls back to the main mailbox so nothing is missed.</p>
        <div className="flex flex-col sm:flex-row gap-3 mt-5">
          <Input data-testid="recipient-name" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="sm:max-w-[200px]" />
          <Input data-testid="recipient-email" type="email" placeholder="email@cass-online.co.uk" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button data-testid="add-recipient-btn" onClick={add} disabled={adding} className="bg-orange hover:bg-orange-dark text-white rounded-full px-6 font-bold shrink-0">
            {adding ? <Loader2 size={16} className="animate-spin" /> : <><Plus size={16} className="mr-1" /> Add</>}
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-400">No recipients yet — add one above.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Recipient</th>
                {PREFS.map((p) => <th key={p.key} className="px-3 py-3 text-center">{p.label}</th>)}
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t border-slate-100" data-testid={`recipient-${item.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-navy">{item.name || "—"}</div>
                    <div className="text-slate-500 text-xs">{item.email}</div>
                  </td>
                  {PREFS.map((p) => (
                    <td key={p.key} className="px-3 py-3 text-center">
                      <input
                        type="checkbox"
                        data-testid={`pref-${item.id}-${p.key}`}
                        checked={!!item.prefs?.[p.key]}
                        onChange={() => togglePref(item, p.key)}
                        className="w-4 h-4 accent-orange cursor-pointer"
                      />
                    </td>
                  ))}
                  <td className="px-3 py-3 text-right">
                    <button data-testid={`remove-recipient-${item.id}`} onClick={() => remove(item.id)} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
